// src/components/GuildBrowser.js

import React, {
  useEffect,
  useState,
} from "react";

import LoadingSpinner
  from "./LoadingSpinner";

const API_BASE =
  process.env.REACT_APP_API_URL ||
  "";

export default function GuildBrowser({
  user,
  onNavigate,
}) {
  const [loading, setLoading] =
    useState(true);

  const [guilds, setGuilds] =
    useState([]);

  const [search, setSearch] =
    useState("");

  const [error, setError] =
    useState("");

  const [busyGuildId, setBusyGuildId] =
    useState(null);

  async function loadGuilds(
    searchValue = ""
  ) {
    const token =
      localStorage.getItem(
        "token"
      );

    if (!token) {
      return;
    }

    try {
      setLoading(true);
      setError("");

      const qs =
        new URLSearchParams();

      if (
        String(
          searchValue || ""
        ).trim()
      ) {
        qs.set(
          "q",
          String(
            searchValue
          ).trim()
        );
      }

      qs.set("limit", "30");
      qs.set("offset", "0");

      const response =
        await fetch(
          `${API_BASE}/api/guilds?${qs.toString()}`,
          {
            headers: {
              Authorization:
                `Bearer ${token}`,

              "X-TG-INITDATA":
                window.Telegram
                  ?.WebApp
                  ?.initData || "",
            },
          }
        );

      const data =
        await response
          .json()
          .catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.error ||
            "GUILD_LIST_FAILED"
        );
      }

      setGuilds(
        Array.isArray(
          data?.guilds
        )
          ? data.guilds
          : []
      );
    } catch (err) {
      console.error(
        "[GUILD BROWSER]",
        err
      );

      setError(
        "Could not load Guilds."
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    loadGuilds();
  }, [user?.id]);

  async function joinGuild(
    guild
  ) {
    if (
      !guild?.id ||
      busyGuildId
    ) {
      return;
    }

    const token =
      localStorage.getItem(
        "token"
      );

    if (!token) {
      return;
    }

    try {
      setBusyGuildId(
        guild.id
      );

      setError("");

      const response =
        await fetch(
          `${API_BASE}/api/guilds/${guild.id}/join`,
          {
            method: "POST",

            headers: {
              "Content-Type":
                "application/json",

              Authorization:
                `Bearer ${token}`,

              "X-TG-INITDATA":
                window.Telegram
                  ?.WebApp
                  ?.initData || "",
            },
          }
        );

      const data =
        await response
          .json()
          .catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.error ||
            "GUILD_JOIN_FAILED"
        );
      }

      /*
       * Open guild:
       * user is now a member.
       */
      if (data?.joined) {
        onNavigate?.(
          "guilds"
        );

        return;
      }

      /*
       * Request guild:
       * refresh to show PENDING.
       */
      if (
        data?.requestPending
      ) {
        await loadGuilds(
          search
        );
      }
    } catch (err) {
      console.error(
        "[GUILD JOIN]",
        err
      );

      const messages = {
        ALREADY_IN_GUILD:
          "You are already in a Guild.",

        GUILD_FULL:
          "This Guild is full.",

        GUILD_INVITE_ONLY:
          "This Guild is invite only.",

        GUILD_LEVEL_REQUIRED:
          "Your level is too low for this Guild.",

        GUILD_TROPHIES_REQUIRED:
          "You do not have enough trophies.",

        GUILD_REQUEST_PENDING:
          "You already have a pending request.",

        GUILD_NOT_FOUND:
          "Guild not found.",
      };

      setError(
        messages[
          err?.message
        ] ||
          "Could not join this Guild."
      );
    } finally {
      setBusyGuildId(null);
    }
  }

  function renderAction(
    guild
  ) {
    const busy =
      busyGuildId ===
      guild.id;

    if (
      guild.action ===
      "member"
    ) {
      return (
        <button
          type="button"
          className="game-button-small"
          onClick={() =>
            onNavigate?.(
              "guilds"
            )
          }
        >
          MY GUILD
        </button>
      );
    }

    if (
      guild.action ===
      "join"
    ) {
      return (
        <button
          type="button"
          disabled={busy}
          className="game-button-small yellow-button"
          onClick={() =>
            joinGuild(guild)
          }
        >
          {busy
            ? "JOINING..."
            : "JOIN"}
        </button>
      );
    }

    if (
      guild.action ===
      "request"
    ) {
      return (
        <button
          type="button"
          disabled={busy}
          className="game-button-small yellow-button"
          onClick={() =>
            joinGuild(guild)
          }
        >
          {busy
            ? "SENDING..."
            : "REQUEST"}
        </button>
      );
    }

    if (
      guild.action ===
      "pending"
    ) {
      return (
        <button
          type="button"
          disabled
          className="game-button-small"
        >
          PENDING
        </button>
      );
    }

    if (
      guild.action ===
      "full"
    ) {
      return (
        <button
          type="button"
          disabled
          className="game-button-small"
        >
          FULL
        </button>
      );
    }

    if (
      guild.action ===
      "invite"
    ) {
      return (
        <button
          type="button"
          disabled
          className="game-button-small"
        >
          INVITE ONLY
        </button>
      );
    }

    return (
      <button
        type="button"
        disabled
        className="game-button-small"
      >
        LOCKED
      </button>
    );
  }

  return (
    <div
      style={{
        width: "100%",
      }}
    >
      <div
        className="modal_title"
        style={{
          position: "relative",
        }}
      >
        FIND A GUILD

        <button
          className="modal_close"
          onClick={() =>
            onNavigate?.(
              "guilds"
            )
          }
        />
      </div>

      <div
        className="modal_body modal_body_2"
      >
        <div
          style={{
            display: "flex",
            gap: 8,
            marginBottom: 12,
          }}
        >
          <input
            type="text"
            value={search}
            placeholder="Search name or tag..."
            onChange={(e) =>
              setSearch(
                e.target.value
              )
            }
            onKeyDown={(e) => {
              if (
                e.key === "Enter"
              ) {
                loadGuilds(
                  search
                );
              }
            }}
            style={{
              flex: 1,
              minWidth: 0,
              height: 42,
              boxSizing:
                "border-box",
            }}
          />

          <button
            type="button"
            className="game-button-small yellow-button"
            onClick={() =>
              loadGuilds(
                search
              )
            }
          >
            SEARCH
          </button>
        </div>

        {error && (
          <div
            style={{
              marginBottom: 10,
              color: "#ff9999",
              textAlign: "center",
            }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <LoadingSpinner />
        ) : guilds.length ===
          0 ? (
          <div
            style={{
              padding: 20,
              textAlign:
                "center",
              color: "#fff",
            }}
          >
            No Guilds found.
          </div>
        ) : (
          guilds.map(
            (guild) => (
              <div
                key={guild.id}
                style={{
                  width: "100%",
                  boxSizing:
                    "border-box",

                  marginBottom: 10,
                  padding: 12,

                  border:
                    "2px solid rgba(205,143,69,.7)",

                  borderRadius: 8,

                  background:
                    "rgba(0,0,0,.25)",
                }}
              >
                <div
                  style={{
                    display:
                      "flex",

                    alignItems:
                      "center",

                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      flex:
                        "1 1 auto",

                      minWidth: 0,
                    }}
                  >
                    <div
                      className="shadow1"
                      style={{
                        color:
                          "#fff",

                        fontSize:
                          17,

                        fontWeight:
                          700,

                        overflow:
                          "hidden",

                        textOverflow:
                          "ellipsis",

                        whiteSpace:
                          "nowrap",
                      }}
                    >
                      {guild.name}

                      <span
                        style={{
                          marginLeft:
                            5,

                          color:
                            "#ffd66c",

                          fontSize:
                            13,
                        }}
                      >
                        [{guild.tag}]
                      </span>
                    </div>

                    <div
                      style={{
                        marginTop:
                          4,

                        color:
                          "rgba(255,255,255,.65)",

                        fontSize:
                          12,
                      }}
                    >
                      {guild.memberCount}
                      /
                      {guild.maxMembers}
                      {" members"}

                      {" • "}

                      {guild.joinType ===
                      "request"
                        ? "Request"
                        : guild.joinType ===
                            "invite"
                          ? "Invite Only"
                          : "Open"}
                    </div>

                    {(Number(
                      guild.minimumLevel
                    ) > 1 ||
                      Number(
                        guild.minimumTrophies
                      ) > 0) && (
                      <div
                        style={{
                          marginTop:
                            3,

                          color:
                            "rgba(255,255,255,.5)",

                          fontSize:
                            11,
                        }}
                      >
                        Level{" "}
                        {guild.minimumLevel}

                        {" • "}

                        Trophies{" "}
                        {guild.minimumTrophies}
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      flex:
                        "0 0 auto",
                    }}
                  >
                    {renderAction(
                      guild
                    )}
                  </div>
                </div>

                {guild.description && (
                  <div
                    style={{
                      marginTop: 8,

                      color:
                        "rgba(255,255,255,.75)",

                      fontSize: 12,

                      lineHeight:
                        "16px",
                    }}
                  >
                    {guild.description}
                  </div>
                )}
              </div>
            )
          )
        )}
      </div>
    </div>
  );
}